import { collection } from './../../../../demo/collections-page/collections.mocks';
import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router, UrlSegment } from '@angular/router';
import { UserProfileInfo } from '@app/core/services/bff/user-profile.service';
import { AccountService, FeaturesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { FeatureEnum, IContentFilterBlock, User } from 'lingo2-models';
import { first } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs';
import { studentTabs, teacherTabs } from './user-profile-nav.constants';

@Component({
  selector: 'app-user-profile-nav',
  templateUrl: './user-profile-nav.component.html',
  styleUrls: ['./user-profile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileNavComponent extends ChangableComponent implements OnInit {
  private location = inject(Location);
  private features = inject(FeaturesService);
  private router = inject(Router);
  deviceService = inject(DeviceDetectorService);

  @Input() public me: User;
  @Input() public set profile(profile: UserProfileInfo) {
    if (this._profile?._id !== profile?._id) {
      this._profile = profile;
      this.init();
    }
  }

  public get profile() {
    return this._profile;
  }

  @Input() public set disciplineChanged(value: string) {
    if (value) {
      this.setActiveTab(first(this.tabs));
    }
  }

  @Input() public user: User;
  @Input() public subjectId: string;
  @Input() public isModal: boolean;
  @Output() activePage = new EventEmitter<string>();

  private _activeTab: string;
  private _profile: UserProfileInfo;
  private subjectsList: number[] = [];
  private initUserId: string = null;
  private profileUrl: string = null;

  public ngOnInit() {
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          if (this.router.url.split('/')[3] === 'feedback') {
            this.setActiveTab(this.tabs.find((t) => t.code === 'feedback'));
            this.detectChanges();
          }
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  public get tabs() {
    const _tabs = AccountService.isAsIfTeacher(this.user) ? teacherTabs : studentTabs;
    return _tabs.filter((t) => {
      if (this.isMe) {
        switch (t.code) {
          case 'collections':
            return this.features.isAvailable(FeatureEnum.profile_collections);

          case 'classes':
            return this.features.isAvailable(FeatureEnum.profile_services);

          case 'schools':
            return this.features.isAvailable(FeatureEnum.profile_schools);

          case 'publications':
            return this.features.isAvailable(FeatureEnum.profile_publications);

          case 'feedback':
            return this.features.isAvailable(FeatureEnum.profile_reviews);

          default:
            return true;
        }
      }

      // остальным показывать только те табы, где у меня есть контент
      switch (t.code) {
        case 'about':
          return !!this.profile?.about;

        case 'lessons':
          return this.profile?.hasLessons && this.profile?.has?.lessons[this.subjectId];

        case 'collections':
          return (
            this.profile?.hasCollections &&
            this.features.isAvailable(FeatureEnum.profile_collections) &&
            this.profile?.has?.collections[this.subjectId]
          );

        case 'classes':
          return (
            this.profile?.hasUserServices &&
            this.features.isAvailable(FeatureEnum.profile_services) &&
            this.profile?.has?.services[this.subjectId]
          );

        case 'schools':
          return this.profile?.hasSchools && this.features.isAvailable(FeatureEnum.profile_schools);

        case 'publications':
          return (
            this.profile?.hasPublications &&
            this.features.isAvailable(FeatureEnum.profile_publications) &&
            this.profile?.has?.publications
          );

        case 'feedback':
          return (
            this.profile?.hasReviews &&
            this.features.isAvailable(FeatureEnum.profile_reviews) &&
            this.profile?.has?.reviews
          );

        default:
          return true;
      }
    });
  }

  public get activeTab() {
    const currentUrl = this.router.url.split('/')[3];

    if (this._activeTab && this.tabs.some((t) => t.code === this._activeTab)) {
      return this._activeTab;
    }

    if (this.tabs.some((t) => t.code === currentUrl)) {
      return currentUrl;
    }

    return first(this.tabs)?.code;
  }

  public routeLinkByItem(item: IContentFilterBlock): UrlSegment {
    if (['teaching', 'classes', 'lessons'].indexOf(item.code) !== -1) {
      return new UrlSegment(item.code + '/' + this.subjectId, {});
    }
    return new UrlSegment(item.code, {});
  }

  public setActiveTab(item: IContentFilterBlock) {
    if (this._activeTab === item.code) {
      return;
    }

    this._activeTab = item.code;
    this.activePage.emit(item.code);
    this.profileUrl = AccountService.accountRoute(this.user).join('/');
    this.location.replaceState(this.profileUrl + '/' + this.routeLinkByItem(item).path);
    this.detectChanges();
  }

  protected init() {
    let subjectsList = [];
    if (AccountService.isAsIfTeacher(this.user)) {
      subjectsList = this._profile?.teachingSubjects?.map((e) => e.subjectId) || [];
    }
    if (this.initUserId === this.user?.id || this.subjectsList.join('+') === subjectsList.join('+')) {
      return;
    }
    this.subjectsList = subjectsList;
    this.initUserId = this.user.id;
  }

  protected get isMe(): boolean {
    return this.user?.id === this.me?.id;
  }
}
